import { useEffect, useMemo, useState } from "react";
import rareMark from "./rare-mark.svg";
import "./style.css";

type ProbeState =
  | { status: "idle" | "loading" }
  | { status: "ok"; count: number; pagination?: unknown; sampleTitle?: string }
  | { status: "error"; message: string };

const DEFAULT_ENDPOINT =
  "https://api.superrare.com/v1/nfts?page=1&perPage=6&q=rare&sortBy=newest";

function describeError(error: unknown): string {
  if (error instanceof Error) return error.message;
  return String(error);
}

export function App() {
  const [endpoint, setEndpoint] = useState(DEFAULT_ENDPOINT);
  const [probe, setProbe] = useState<ProbeState>({ status: "idle" });
  const deployedAt = useMemo(() => new Date().toISOString(), []);

  async function runProbe(url = endpoint) {
    setProbe({ status: "loading" });
    try {
      const response = await fetch(url, {
        headers: { Accept: "application/json" },
      });
      if (!response.ok) {
        throw new Error(`${response.status} ${response.statusText}`);
      }

      const payload = await response.json();
      const data = Array.isArray(payload?.data) ? payload.data : [];
      const sample = data[0];
      const title =
        sample?.title ??
        sample?.name ??
        sample?.metadata?.name ??
        sample?.tokenName ??
        undefined;

      setProbe({
        status: "ok",
        count: data.length,
        pagination: payload?.pagination,
        sampleTitle: typeof title === "string" ? title : undefined,
      });
    } catch (error) {
      setProbe({ status: "error", message: describeError(error) });
    }
  }

  useEffect(() => {
    void runProbe(DEFAULT_ENDPOINT);
  }, []);

  return (
    <main className="app-shell">
      <section className="hero">
        <div className="brand-row">
          <img src={rareMark} alt="" className="mark" />
          <div>
            <p className="eyebrow">Watchtower Smoke Test</p>
            <h1>Vite static app deploy probe</h1>
          </div>
        </div>
        <p className="summary">
          This app is intentionally small. It verifies that a Vite build can be
          zipped, deployed through `rare-profile app deploy`, load relative
          assets, run module JavaScript, and call Rare read APIs directly from
          the browser.
        </p>
      </section>

      <section className="grid">
        <article className="panel">
          <h2>Runtime Checks</h2>
          <dl className="checks">
            <div>
              <dt>Build tool</dt>
              <dd>Vite + React</dd>
            </div>
            <div>
              <dt>Module script</dt>
              <dd>Loaded</dd>
            </div>
            <div>
              <dt>Relative asset</dt>
              <dd>SVG mark rendered</dd>
            </div>
            <div>
              <dt>Built at</dt>
              <dd>{deployedAt}</dd>
            </div>
          </dl>
        </article>

        <article className="panel">
          <h2>Rare API Probe</h2>
          <label className="endpoint-label" htmlFor="endpoint">
            Endpoint
          </label>
          <textarea
            id="endpoint"
            value={endpoint}
            onChange={(event) => setEndpoint(event.target.value)}
            spellCheck={false}
          />
          <button type="button" onClick={() => void runProbe()}>
            Run Fetch Probe
          </button>

          <div className={`probe-result probe-${probe.status}`}>
            {probe.status === "idle" && "Probe has not run yet."}
            {probe.status === "loading" && "Fetching Rare API data..."}
            {probe.status === "ok" && (
              <>
                <strong>Fetch succeeded.</strong>
                <span>{probe.count} NFTs returned.</span>
                {probe.sampleTitle && <span>Sample: {probe.sampleTitle}</span>}
                {probe.pagination && (
                  <code>{JSON.stringify(probe.pagination)}</code>
                )}
              </>
            )}
            {probe.status === "error" && (
              <>
                <strong>Fetch failed.</strong>
                <span>{probe.message}</span>
              </>
            )}
          </div>
        </article>
      </section>
    </main>
  );
}

